import { useEffect, useMemo, useRef } from "react";
import "./Otp.css";

// =====================================================
// OTP CODE INPUT
//
// The one code-entry control in the app. The registration screen, the
// forgot-password screen and the bank/withdrawal dialog all render THIS — the
// same reason the backend has one OTP service: three hand-rolled copies is
// three places for the paste handling to be subtly different.
//
// ── WHAT IT ACTUALLY HAS TO GET RIGHT ──
// A code arrives in a notification the user wants to paste, or they read it off
// a second device and type it. So:
//   • Paste fills the whole code from ANY box, not just the first, and strips
//     everything that is not a digit — "482 913" and "Code: 482913" both work.
//   • Typing advances; Backspace on an empty box steps back and clears the one
//     before it, which is what people expect and what a naive implementation
//     gets wrong.
//   • Arrow keys move the caret between boxes.
//   • inputMode="numeric" + autoComplete="one-time-code" is what lets iOS and
//     Chrome offer the code from the mail/SMS directly above the keyboard.
//
// The value is owned by the PARENT (a plain string). This component renders it
// across boxes and reports changes; it keeps no copy of its own, so a parent
// that clears the field after a wrong code really does clear it.
// =====================================================

function OtpCodeInput({
  value = "",
  onChange,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = true,
  // Fired when the last box is filled, so the form can submit itself rather
  // than making the user reach for a button they were always going to press.
  onComplete,
  label = "Verification code",
}) {
  const refs = useRef([]);
  const digits = useMemo(() => {
    const clean = String(value || "").replace(/\D/g, "").slice(0, length);
    return Array.from({ length }, (_, i) => clean[i] || "");
  }, [value, length]);

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const emit = (next) => {
    const joined = next.join("").slice(0, length);
    onChange(joined);
    if (joined.length === length && onComplete) onComplete(joined);
  };

  const focusBox = (i) => {
    const box = refs.current[Math.max(0, Math.min(length - 1, i))];
    if (box) {
      box.focus();
      box.select();
    }
  };

  const handleChange = (index) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      // The box was cleared.
      const next = [...digits];
      next[index] = "";
      emit(next);
      return;
    }

    // More than one digit in a single box means the OS autofilled the whole
    // code into it (Android does this). Spread it across the remaining boxes.
    const next = [...digits];
    for (let i = 0; i < raw.length && index + i < length; i += 1) {
      next[index + i] = raw[i];
    }
    emit(next);
    focusBox(index + raw.length);
  };

  const handleKeyDown = (index) => (e) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        emit(next);
        return;
      }
      // Empty box — step back AND clear the previous digit in one press.
      e.preventDefault();
      if (index > 0) {
        const next = [...digits];
        next[index - 1] = "";
        emit(next);
        focusBox(index - 1);
      }
      return;
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  // Paste works from any box: the pasted digits always fill from the start,
  // because a pasted code is the WHOLE code — not a continuation of what is
  // already typed.
  const handlePaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData("text") || "";
    const clean = text.replace(/\D/g, "").slice(0, length);
    if (!clean) return;
    e.preventDefault();
    const next = Array.from({ length }, (_, i) => clean[i] || "");
    emit(next);
    focusBox(Math.min(clean.length, length - 1));
  };

  return (
    <div className="otp-boxes-wrap">
      <div
        className={`otp-boxes${invalid ? " otp-boxes-invalid" : ""}`}
        role="group"
        aria-label={label}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={`otp-box${digit ? " otp-box-filled" : ""}`}
            type="text"
            inputMode="numeric"
            /* The browser/OS offers the emailed code above the keyboard. */
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            aria-label={`${label} digit ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default OtpCodeInput;
